import { Controller } from 'stimulus';

// The measurement editor (measurements/_editor): one dialog for task
// measurements and result columns. Shows the sections the chosen Field Type
// uses, lays out the pass/fail rule as one row with a plain-language result
// under it, and edits a dropdown's options as a list with Pass / Fail / Not
// judged on each row, written back to the comma-separated dropdown_options,
// dropdown_pass and dropdown_fail fields the server already reads.
//
// Sits on the wrapper around the form so it hears the form's submit.
const LIMITS = {
  maximum: [['maximum', 'Maximum']],
  minimum: [['minimum', 'Minimum']],
  between: [['value_one', 'From'], ['value_two', 'To']],
  outside: [['value_one', 'From'], ['value_two', 'To']],
  equal: [['value_one', 'Value']],
};

const OFF_NOTES = {
  number: 'Not judged: the value is recorded as entered. Turn on to set a limit.',
  formula: 'Not judged: the result is shown but not marked pass or fail.',
  dropdown: 'Not judged: any option can be chosen.',
};

const VERDICTS = [['p', 'Pass'], ['f', 'Fail'], ['n', 'Not judged']];

export default class extends Controller {
  static targets = [
    'form', 'fieldType', 'section', 'conditions', 'ruleOn', 'ruleOff', 'filterType', 'limit', 'ruleUnit', 'verdict',
    'optionsField', 'passField', 'failField', 'optionList', 'newOption',
  ];

  connect() {
    if (this.hasOptionListTarget) {
      this.renderOptions();
      $(this.optionListTarget).sortable({
        axis: 'y',
        handle: '.me-option__grip',
        tolerance: 'pointer',
        items: '> .me-option',
        stop: () => this.syncOptions(),
      });
    }
    this.update();
  }

  disconnect() {
    if (this.hasOptionListTarget && $(this.optionListTarget).sortable('instance')) {
      $(this.optionListTarget).sortable('destroy');
    }
  }

  get type() {
    return this.fieldTypeTarget.value;
  }

  get judged() {
    return this.hasConditionsTarget && this.conditionsTarget.checked;
  }

  changeType() {
    this.update();
  }

  changeConditions() {
    this.update();
  }

  changeFilter() {
    this.updateFilter();
    this.describeRule();
  }

  update() {
    const type = this.type;
    this.sectionTargets.forEach((section) => {
      const types = (section.dataset.types || '').split(' ');
      section.classList.toggle('me-hide', !types.includes(type));
    });

    const judged = this.judged;
    this.ruleOnTarget.classList.toggle('me-hide', !judged);
    this.ruleOffTarget.classList.toggle('me-hide', judged);
    this.ruleOffTarget.textContent = OFF_NOTES[type] || '';
    if (this.hasOptionListTarget) this.optionListTarget.classList.toggle('me-options--unjudged', !judged);

    this.updateFilter();
    this.describeRule();
  }

  // Shows the limit inputs the chosen rule uses: one for Maximum, Minimum
  // and Equal To, two for Between and Outside.
  updateFilter() {
    const shown = LIMITS[this.filterTypeTarget.value] || [];
    const names = shown.map(([name]) => name);
    this.limitTargets.forEach((container) => {
      const index = names.indexOf(container.dataset.limit);
      container.classList.toggle('me-hide', index === -1);
      if (index !== -1) container.querySelector('input').placeholder = shown[index][1];
    });
    this.element.querySelector('.me-rule').classList.toggle('me-rule--two', shown.length === 2);
  }

  // "Pass at 0.304 or below / Fail above 0.304", from the rule as typed.
  // Ranges include their end points (EvaluateFilters).
  describeRule() {
    if (!this.hasVerdictTarget) return;

    const unit = this.unitAbbreviation();
    if (this.hasRuleUnitTarget) this.ruleUnitTarget.textContent = unit;

    const filter = this.filterTypeTarget.value;
    const [first, second] = (LIMITS[filter] || []).map(([name]) => this.limitValue(name));
    const u = unit ? ` ${unit}` : '';
    if (!first || ((filter === 'between' || filter === 'outside') && !second)) {
      this.verdictTarget.innerHTML = '<span class="me-hint">Enter a limit to see what passes.</span>';
      return;
    }

    const a = `${first}${u}`;
    const b = second ? `${second}${u}` : '';
    const lines = {
      maximum: [`Pass at ${a} or below`, `Fail above ${a}`],
      minimum: [`Pass at ${a} or above`, `Fail below ${a}`],
      between: [`Pass from ${first} to ${b}`, 'Fail outside it'],
      outside: [`Pass below ${a} or above ${b}`, `Fail from ${first} to ${b}`],
      equal: [`Pass at exactly ${a}`, 'Fail otherwise'],
    }[filter];
    if (!lines) {
      this.verdictTarget.innerHTML = '';
      return;
    }

    this.verdictTarget.innerHTML =
      `<span class="me-verdict__item bg-green-100 text-green-800">${this.escape(lines[0])}</span>` +
      `<span class="me-verdict__item bg-red-100 text-red-800">${this.escape(lines[1])}</span>`;
  }

  limitValue(name) {
    const container = this.limitTargets.find((c) => c.dataset.limit === name);
    return container ? container.querySelector('input').value.trim() : '';
  }

  // The unit picker's choice as its short form: "Meters (m)" reads "m".
  unitAbbreviation() {
    const select = this.element.querySelector('[data-target="category-unit.unitSelect"]');
    if (!select || !select.value || select.closest('.me-hide')) return '';

    const label = select.options[select.selectedIndex].text;
    const match = label.match(/\(([^)]+)\)\s*$/);
    return match ? match[1] : label;
  }

  // ----- dropdown options -----

  parseList(value) {
    return (value || '').split(',').map((s) => s.trim()).filter(Boolean);
  }

  renderOptions() {
    const pass = this.parseList(this.passFieldTarget.value);
    const fail = this.parseList(this.failFieldTarget.value);
    this.optionListTarget.innerHTML = '';
    this.parseList(this.optionsFieldTarget.value).forEach((label) => {
      let verdict = 'n';
      if (pass.includes(label)) verdict = 'p';
      else if (fail.includes(label)) verdict = 'f';
      this.optionListTarget.appendChild(this.buildOption(label, verdict));
    });
  }

  buildOption(label, verdict) {
    const row = document.createElement('div');
    row.className = 'me-option';
    row.dataset.verdict = verdict;

    const grip = document.createElement('span');
    grip.className = 'me-option__grip';
    grip.setAttribute('aria-hidden', 'true');
    grip.textContent = '⋮⋮';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'me-option__label';
    input.value = label;
    input.setAttribute('value', label);
    input.setAttribute('aria-label', 'Option');
    input.dataset.action = 'input->measurement-editor#editOption keydown->measurement-editor#optionKey';

    const seg = document.createElement('span');
    seg.className = 'me-seg';
    VERDICTS.forEach(([key, text]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.verdict = key;
      button.dataset.action = 'measurement-editor#setVerdict';
      button.textContent = text;
      seg.appendChild(button);
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'me-option__remove';
    remove.setAttribute('aria-label', `Remove ${label}`);
    remove.dataset.action = 'measurement-editor#removeOption';
    remove.innerHTML = '&times;';

    row.append(grip, input, seg, remove);
    this.paintVerdict(row);
    return row;
  }

  paintVerdict(row) {
    row.querySelectorAll('.me-seg button').forEach((button) => {
      const on = button.dataset.verdict === row.dataset.verdict;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', on);
      button.classList.toggle('bg-green-100', on && button.dataset.verdict === 'p');
      button.classList.toggle('text-green-800', on && button.dataset.verdict === 'p');
      button.classList.toggle('bg-red-100', on && button.dataset.verdict === 'f');
      button.classList.toggle('text-red-800', on && button.dataset.verdict === 'f');
    });
  }

  setVerdict(event) {
    const row = event.currentTarget.closest('.me-option');
    row.dataset.verdict = event.currentTarget.dataset.verdict;
    this.paintVerdict(row);
    this.syncOptions();
  }

  removeOption(event) {
    event.currentTarget.closest('.me-option').remove();
    this.syncOptions();
  }

  // Options are stored comma-separated, so a comma can't be part of one.
  editOption(event) {
    const input = event.currentTarget;
    if (input.value.includes(',')) input.value = input.value.replace(/,/g, '');
    this.syncOptions();
  }

  optionKey(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.newOptionTarget.focus();
    }
  }

  addOptionOnEnter(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    this.addOption();
  }

  // Adds what is typed in "Add an option"; a pasted "A, B, C" adds three.
  addOption() {
    // Leaving the box empty changes nothing, so a re-save keeps the stored
    // option string exactly as it was (a rewrite would re-judge the column).
    if (!this.hasNewOptionTarget || !this.newOptionTarget.value.trim()) return;

    const existing = this.optionLabels();
    this.parseList(this.newOptionTarget.value).forEach((label) => {
      if (existing.includes(label)) return;
      existing.push(label);
      this.optionListTarget.appendChild(this.buildOption(label, 'n'));
    });
    this.newOptionTarget.value = '';
    this.syncOptions();
  }

  optionLabels() {
    return Array.from(this.optionListTarget.querySelectorAll('.me-option__label')).map((i) => i.value.trim()).filter(Boolean);
  }

  syncOptions() {
    const rows = Array.from(this.optionListTarget.querySelectorAll('.me-option'))
      .map((row) => [row.querySelector('.me-option__label').value.trim(), row.dataset.verdict])
      .filter(([label]) => label);
    this.optionsFieldTarget.value = rows.map(([label]) => label).join(', ');
    this.passFieldTarget.value = rows.filter(([, v]) => v === 'p').map(([label]) => label).join(', ');
    this.failFieldTarget.value = rows.filter(([, v]) => v === 'f').map(([label]) => label).join(', ');
  }

  // ----- submit -----

  // Blanks what the chosen type and rule don't use, as the old form did:
  // inputs in hidden sections, the rule when pass/fail is off, and the
  // limits the chosen rule doesn't read. Hidden unit and formula inputs are
  // kept, as before.
  cleanForm() {
    if (this.hasNewOptionTarget && this.newOptionTarget.value.trim()) this.addOption();

    this.sectionTargets.filter((s) => s.classList.contains('me-hide')).forEach((section) => this.blank(section));
    if (!this.judged) {
      this.blank(this.ruleOnTarget);
      this.filterTypeTarget.value = '';
      if (this.hasPassFieldTarget) {
        this.passFieldTarget.value = '';
        this.failFieldTarget.value = '';
      }
    } else {
      this.limitTargets.filter((c) => c.classList.contains('me-hide')).forEach((c) => this.blank(c));
    }
    if (this.type !== 'dropdown' && this.hasOptionsFieldTarget) {
      this.optionsFieldTarget.value = '';
      this.passFieldTarget.value = '';
      this.failFieldTarget.value = '';
    }
  }

  blank(container) {
    container.querySelectorAll('input[type=text], input[type=number]').forEach((i) => { i.value = ''; });
    container.querySelectorAll('input[type=checkbox]').forEach((i) => { i.checked = false; });
    container.querySelectorAll('select').forEach((s) => {
      if (!s.dataset.target || !s.dataset.target.startsWith('category-unit')) s.value = '';
    });
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
